/**
 * glTF 2.1 type definitions and the normalized scene-document shape.
 *
 * glTF 2.1 (draft, Khronos `draft-2.1`) folds glXF's composition features into
 * core: a document lists every file it depends on in `files[]`, and a node
 * instantiates a glTF/GLB file through `externalAssets[]`. See
 * `docs-internal/GLTF_2_1_MIGRATION_PLAN.md` §1 for the mapping from glXF and
 * `docs-internal/CONTENT_MODEL_SPEC.md` §2.1 for the folder layout the URIs
 * are resolved against.
 *
 * Property names are provisional until ratification (plan §6, "draft drift").
 * Everything that reads them funnels through `normalizeSceneDocument`, so a
 * rename is a one-module change.
 */

import type * as GLTF2 from 'babylonjs-gltf2interface';

// =============================================================================
// Wire types — glTF 2.1
// =============================================================================

/** Registered media type of a glTF JSON document. */
export const GLTF_JSON_MIME = 'model/gltf+json';
/** Registered media type of a binary glTF (GLB). */
export const GLTF_BINARY_MIME = 'model/gltf-binary';

/**
 * One entry of the 2.1 `files[]` manifest: the single list of everything the
 * document depends on. `mimeType` is required by the spec and is what policy
 * keys on (spec §2.5) — never the URI string.
 */
export interface IGLTFFile {
    /** Location of the file. Mutually exclusive with `bufferView` (packaging). */
    uri?: string;
    /** Packaged file bytes (glTF 2.1 packaging; not read yet — spec §4). */
    bufferView?: number;
    /** Required by the spec for every entry. */
    mimeType?: string;
    /**
     * The file's ORIGINAL human name (`chair.glb`) — informational only
     * (spec §2.1, §3.6).
     *
     * Storage names files by hash and never renames; this is what the user
     * dropped, echoed back by `POST /files`. Nothing RESOLVES by it: the
     * bundle writer (§4) uses it to name archive entries and an editor shows
     * it, and that is the whole contract.
     */
    name?: string;
    extras?: unknown;
}

/**
 * A file instantiated as a sub-scene. The referenced file must be
 * `model/gltf+json` or `model/gltf-binary`; images, video and ZIP scenes
 * cannot be external assets and travel via `extras.trace.file` instead
 * (plan D3).
 */
export interface IGLTFExternalAsset {
    /** Index into `files`. */
    file: number;
    name?: string;
    extras?: unknown;
}

/** A glTF 2.1 node. `externalAsset` cannot coexist with `mesh`. */
export interface IGLTF21Node {
    name?: string;
    /**
     * glTF 2.1 node identity: an alphanumeric string, unique across every
     * `uid` and `name` in the document (issue #837, SCENE_ROOT_SPEC §2.3).
     *
     * Optional in 2.1 and absent from every glXF document; a reader falls back
     * to a qualifying `name` (see `normalizeSceneDocument`).
     */
    uid?: string;
    children?: number[];
    camera?: number;
    mesh?: number;
    /** Index into `externalAssets`. */
    externalAsset?: number;
    matrix?: number[];
    rotation?: number[];
    scale?: number[];
    translation?: number[];
    extras?: unknown;
    /**
     * Node-level extensions. Nothing in the writer generates one; the field
     * exists so a foreign block a document arrived with is written back
     * (issue #834, spec §3.6).
     */
    extensions?: Record<string, unknown>;
}

/** A glTF 2.1 scene-composition document (the "scene root" of SCENE_ROOT_SPEC). */
export interface IGLTF21 {
    asset: GLTF2.IAsset;
    files?: IGLTFFile[];
    externalAssets?: IGLTFExternalAsset[];
    nodes?: IGLTF21Node[];
    scene?: number;
    scenes?: GLTF2.IScene[];
    extensionsUsed?: string[];
    extensionsRequired?: string[];
    extensions?: Record<string, unknown>;
    extras?: unknown;
}

// =============================================================================
// Normalized shape — what every reader consumes
// =============================================================================

/** Which wire format a normalized document was read from. */
export type SceneDocumentFormat = 'glxf' | 'gltf21';

/** How a node names the file it sources. */
export type SceneFileCarrier =
    /** `node.externalAsset` (2.1) or `node.asset` (glXF) — a glTF/GLB sub-scene. */
    | 'externalAsset'
    /** `node.extras.trace.file` — image, video, audio, or a nested Trace scene. */
    | 'trace';

/** One resolved entry of the dependency manifest. */
export interface NormalizedFile {
    /** Verbatim URI as authored — relative entries stay relative (spec §2.3). */
    uri: string;
    /**
     * Declared `mimeType`, or one inferred from the URI extension when the
     * document is legacy glXF (which has no manifest). Empty when unknown.
     */
    mimeType: string;
    /**
     * `files[k].name` — the original human file name, informational (§2.1).
     * Absent on glXF, which has no manifest and no such field.
     */
    name?: string;
}

/** A node with its file reference resolved to a manifest index. */
export interface NormalizedNode {
    name: string;
    /**
     * glTF 2.1 node identity (`node.uid`), or the node's `name` when 2.1's
     * fallback rule applies to it — see `normalizeSceneDocument`. Never set
     * for a legacy glXF document, which has no such concept and is never
     * stamped (issue #837).
     */
    uid?: string;
    children?: number[];
    translation?: number[];
    rotation?: number[];
    scale?: number[];
    matrix?: number[];
    /** Index into {@link NormalizedSceneDocument.files}, when this node sources one. */
    file?: number;
    /** How the file was referenced. Present iff `file` is. */
    carrier?: SceneFileCarrier;
    /** Verbatim `node.extras` (Trace payload lives under `extras.trace`). */
    extras?: Record<string, unknown>;
    /**
     * Verbatim `node.extensions`. Nothing here reads one — it is carried so a
     * load → save round trip puts it back (issue #834, spec §3.6). The one
     * extension the reader DOES own, `TRACE_animation_binding`, is resolved into
     * {@link animationBinding} and is not foreign.
     */
    extensions?: Record<string, unknown>;
    /**
     * `node.extensions.TRACE_animation_binding`, with each source's
     * `externalAsset` resolved to its manifest index (`files[k]`). Present iff
     * the node carries the extension with at least one valid source.
     */
    animationBinding?: NormalizedAnimationBinding;
}

/** `TRACE_animation_binding` on a node, resolved against the manifest. */
export interface NormalizedAnimationBinding {
    sources: Array<{ file: number; root?: string }>;
    /** Entries dropped as malformed or naming no file. */
    problems: string[];
}

/** A scene entry. Root data lives in `extras.trace` (SCENE_ROOT_SPEC §2.1). */
export interface NormalizedScene {
    nodes: number[];
    name?: string;
    extras?: Record<string, unknown>;
}

/**
 * The one shape every consumer of a scene document reads: the loader plugin,
 * the exporter's inverse, and the server-side migration job (WP-E), which
 * relies on this being a pure, deterministic function of the input.
 */
export interface NormalizedSceneDocument {
    /** Wire format this was read from. */
    sourceFormat: SceneDocumentFormat;
    /** Dependency manifest, in document order. */
    files: NormalizedFile[];
    /** Nodes, in document order; `children` indices address this array. */
    nodes: NormalizedNode[];
    /** Default scene index. */
    scene: number;
    scenes: NormalizedScene[];
    /** Document-level extensions (`KHR_interactivity` is restored from here). */
    extensions?: Record<string, unknown>;
    /** Document-level extras (`extras.trace.{version,name,scripts}`). */
    extras?: Record<string, unknown>;
    /**
     * `extensionsUsed` / `extensionsRequired`, verbatim. Carried for the same
     * reason as the extensions themselves: a declaration the writer drops is a
     * document that no longer says what it needs (issue #834, spec §3.6).
     */
    extensionsUsed?: string[];
    extensionsRequired?: string[];
    /** Verbatim `asset.extras` — foreign by definition; the writer authors none. */
    assetExtras?: Record<string, unknown>;
}
